/**
 * 🚀 SMART TABLE - File Intelligence Table
 *
 * Premium UI for displaying file nano-metadata with:
 * - DNA column (file-specific insights)
 * - Status badges
 * - Health bars
 * - Micro-insights
 * - Real-time streaming animation
 *
 * @module SmartTable
 */

import React from 'react';

import DNAColumn from '@/components/smart-notebook/SmartTable/DNAColumn';
import HealthBar from '@/components/smart-notebook/SmartTable/HealthBar';
import SmartRow from '@/components/smart-notebook/SmartTable/SmartRow';
import StatusBadge from '@/components/smart-notebook/SmartTable/StatusBadge';
import '@/components/smart-notebook/SmartTable/SmartTable.css';

/**
 * SmartTable Component
 * @param {Object} props
 * @param {Array} props.rows - Array of nano-metadata rows
 * @param {Function} props.onRowClick - Optional click handler for row
 * @param {Function} props.onRemoveRow - Optional remove handler
 * @param {Function} props.onRetryRow - Optional retry handler for failed uploads
 * @param {boolean} props.compact - Use compact mode
 * @param {string} props.className - Additional CSS classes
 */
export default function SmartTable({
  rows = [],
  onRowClick,
  onRemoveRow,
  onRetryRow,
  compact = false,
  className = '',
}) {
  // Count stats (deterministic based on row.status)
  const loadingCount = rows.filter(
    r => r.status === 'uploading' || r.status === 'processing' || r.status === 'queued'
  ).length;
  const errorCount = rows.filter(r => r.status === 'error').length;
  const readyCount = rows.filter(r => r.status === 'ready').length;

  if (rows.length === 0) {
    return (
      <div className={`smart-table-empty ${className}`}>
        <div className="smart-table-empty-icon">📄</div>
        <p className="smart-table-empty-text">No files analyzed yet</p>
        <p className="smart-table-empty-hint">Upload files to see their DNA instantly</p>
      </div>
    );
  }

  return (
    <div className={`smart-table ${compact ? 'smart-table-compact' : ''} ${className}`}>
      <div className="smart-table-header">
        <div className="header-cell header-doc">Document</div>
        <div className="header-cell header-dna">DNA</div>
        <div className="header-cell header-status">Status</div>
        <div className="header-cell header-health">Health</div>
        <div className="header-cell header-insight">Instant Insight</div>
      </div>

      <div className="smart-table-body">
        {rows.map((row, index) => (
          <SmartRow
            key={row.file_id || `row-${index}`}
            row={row}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            onRemove={onRemoveRow ? () => onRemoveRow(row.file_id) : undefined}
            onRetry={onRetryRow ? () => onRetryRow(row.file_id) : undefined}
            compact={compact}
          />
        ))}
      </div>

      <div className="smart-table-footer">
        <span className="smart-table-count">
          {rows.length} file{rows.length !== 1 ? 's' : ''}
          {loadingCount > 0 && ` • ${loadingCount} analyzing`}
          {errorCount > 0 && ` • ${errorCount} failed`}
          {readyCount > 0 && ` • ${readyCount} ready`}
        </span>
        <span className="smart-table-engine">⚡ Nano-Metadata Engine</span>
      </div>
    </div>
  );
}

// Re-export components (they use export default, so we import and re-export)
export { default as SmartRow } from '@/components/smart-notebook/SmartTable/SmartRow';
export { default as StatusBadge } from './StatusBadge';
export { default as HealthBar } from './HealthBar';
export { default as DNAColumn } from './DNAColumn';
export { default as FileIcon } from './FileIcon';
export { default as InsightCell } from './InsightCell';
